/**
 * ReviewStep - Final step: Review and launch content creation
 */

import React, { useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  FileText,
  LayoutTemplate,
  Edit,
  ListPlus,
  Layers,
  Sparkles,
  CheckCircle2,
  Globe,
  FileDown,
  FilePen,
} from 'lucide-react';
import { useBlogTemplate } from '@/hooks/useBlogTemplates';
import { cn } from '@/lib/utils';
import type { WizardState, PublishDestination } from '../ContentPlannerWizard';

interface ReviewStepProps {
  wizardState: WizardState;
  onEdit: (stepIndex: number) => void;
  onUpdateWordCount: (count: number) => void;
  onUpdatePublishDestination: (dest: PublishDestination) => void;
}

const WORD_COUNT_PRESETS = [600, 800, 1000, 1200, 1500, 1800, 2000, 2500];

const PUBLISH_OPTIONS: {
  value: PublishDestination;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'none',
    label: 'App Draft Only',
    description: 'Keep it in AgileCo only; no WordPress sync',
    icon: <FilePen className="h-5 w-5" />,
  },
  {
    value: 'wp_draft',
    label: 'WordPress Draft',
    description: 'Sync to WordPress as a draft, not live',
    icon: <Globe className="h-5 w-5" />,
  },
  {
    value: 'gdocs',
    label: 'Google Doc Only',
    description: 'Export to your connected Google Drive folder',
    icon: <FileDown className="h-5 w-5" />,
  },
  {
    value: 'wp_draft_gdocs',
    label: 'WordPress Draft + Google Doc',
    description: 'Sync a WordPress draft and export to Google Docs',
    icon: <Globe className="h-5 w-5" />,
  },
  {
    value: 'wp_publish',
    label: 'Publish to WordPress',
    description: 'Auto-publish to WordPress after generation',
    icon: <Globe className="h-5 w-5" />,
  },
  {
    value: 'wp_publish_gdocs',
    label: 'Publish + Google Doc',
    description: 'Publish live to WordPress and export to Google Docs',
    icon: <Globe className="h-5 w-5" />,
  },
];

const ReviewStep: React.FC<ReviewStepProps> = ({
  wizardState,
  onEdit,
  onUpdateWordCount,
  onUpdatePublishDestination,
}) => {
  const { data: selectedTemplate } = useBlogTemplate(wizardState.templateId);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync input display value when template changes word count
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = String(wizardState.targetWordCount);
    }
  }, [wizardState.targetWordCount]);

  const handleWordCountInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 100 && val <= 10000) {
      onUpdateWordCount(val);
    }
  };

  const isPathA = wizardState.entryPoint === 'have-keywords';

  const primaryKeyword = wizardState.keywords[0] || '';
  const secondaryKeywords = wizardState.keywords.slice(1);
  const totalClusters = wizardState.selectedClusters.length;
  const totalVolume = wizardState.selectedClusters.reduce((sum, c) => sum + c.total_volume, 0);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/40 mb-4">
          <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-semibold mb-2">Almost there!</h2>
        <p className="text-sm text-muted-foreground">
          Confirm your settings, then click "Create Content" to start generation
        </p>
      </div>

      {/* ── Word count ───────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Target word count</Label>
        <div className="flex items-center gap-3">
          <Input
            ref={inputRef}
            type="number"
            min={100}
            max={10000}
            step={100}
            defaultValue={wizardState.targetWordCount}
            onChange={handleWordCountInput}
            className="w-28"
          />
          <span className="text-sm text-muted-foreground">words</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {WORD_COUNT_PRESETS.map((count) => (
            <button
              key={count}
              onClick={() => onUpdateWordCount(count)}
              className={cn(
                'px-2.5 py-1 rounded text-xs font-medium border transition-colors',
                wizardState.targetWordCount === count
                  ? 'bg-teal text-white border-teal'
                  : 'bg-background border-border text-muted-foreground hover:border-teal hover:text-foreground',
              )}
            >
              {count.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {/* ── Publish destination ───────────────────────────────────────────── */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">After generation</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PUBLISH_OPTIONS.map((opt) => {
            const selected = wizardState.publishDestination === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onUpdatePublishDestination(opt.value)}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                  selected
                    ? 'border-teal bg-teal-light'
                    : 'border-border bg-background hover:border-teal/50',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 shrink-0',
                    selected ? 'text-teal-dark' : 'text-muted-foreground',
                  )}
                >
                  {opt.icon}
                </span>
                <div>
                  <div
                    className={cn(
                      'text-sm font-medium',
                      selected ? 'text-teal-dark' : 'text-foreground',
                    )}
                  >
                    {opt.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {/* Path Type */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="font-medium">Content Creation Path</div>
              <div className="text-sm text-muted-foreground">
                {isPathA ? 'Direct keyword entry' : 'Research-driven'}
              </div>
            </div>
          </div>
        </Card>

        {/* Keywords */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal/20 rounded-lg">
                <ListPlus className="h-5 w-5 text-teal" />
              </div>
              <div className="flex-1">
                <div className="font-medium mb-1">Keywords</div>
                <div className="text-sm text-muted-foreground">
                  <div className="mb-1">
                    Primary: <span className="font-medium text-foreground">{primaryKeyword}</span>
                  </div>
                  {secondaryKeywords.length > 0 && (
                    <div>
                      +{secondaryKeywords.length} secondary:{' '}
                      <span className="text-foreground">
                        {secondaryKeywords.slice(0, 3).join(', ')}
                        {secondaryKeywords.length > 3 && '...'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(isPathA ? 1 : 3)}>
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        {/* Clusters (Path B only) */}
        {!isPathA && totalClusters > 0 && (
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                  <Layers className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="font-medium mb-1">Selected Clusters</div>
                  <div className="text-sm text-muted-foreground">
                    {totalClusters} cluster{totalClusters !== 1 ? 's' : ''} selected
                    {' • '}
                    {totalVolume.toLocaleString()} total volume
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onEdit(2)}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Template */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-lg">
                {selectedTemplate?.icon ? (
                  <span className="text-lg">{selectedTemplate.icon}</span>
                ) : (
                  <LayoutTemplate className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                )}
              </div>
              <div>
                <div className="font-medium mb-1">Template</div>
                <div className="text-sm text-muted-foreground">
                  {selectedTemplate ? (
                    <>{selectedTemplate.name}</>
                  ) : (
                    'Default blog format'
                  )}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onEdit(isPathA ? 2 : 4)}>
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        {/* Output summary */}
        <Card className="p-4 bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="font-medium mb-1 text-green-900 dark:text-green-300">What will be created</div>
              <div className="text-sm text-green-700 dark:text-green-400">
                {isPathA ? (
                  <>
                    <strong>1 blog post</strong> · "{primaryKeyword}"
                    {secondaryKeywords.length > 0 && (
                      <> + {secondaryKeywords.length} secondary keyword{secondaryKeywords.length > 1 ? 's' : ''}</>
                    )}
                    {' · '}~{wizardState.targetWordCount.toLocaleString()} words
                  </>
                ) : (
                  <>
                    <strong>{totalClusters} blog post{totalClusters !== 1 ? 's' : ''}</strong> from selected clusters
                    {' · '}~{wizardState.targetWordCount.toLocaleString()} words each
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="text-center text-sm text-muted-foreground">
        Content generation typically takes 15–20 minutes per post. You can
        close this and keep working — the post appears in the planner when done.
      </div>
    </div>
  );
};

export default ReviewStep;
